import { Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import {
    formatDataGeneral,
    getAttributeValue,
    getFileLinkIfExist,
    getFirstFileLinkIfExist,
} from "utils/utils";
import {
    renderDpatEvidenceLink,
    scoreOnlyIfDocumentLinksComplete,
} from "utils/renderDpatEvidenceLink";
import { documentsColumn } from "utils/tableColums";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";

const PROJECT_PROGRAM = "g3wMUKEMmH3";

function fileUrl(eventId, dataElementUid) {
    return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementUid}`;
}

function getYearFromAttributeValue(value) {
    if (value == null || value === "" || value === "N/A") return null;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.getFullYear();
    const match = String(value).match(/\b(19|20)\d{2}\b/);
    return match ? Number(match[0]) : null;
}

function isProjectActiveInYear(item, selectedYear) {
    const year = Number(selectedYear);
    if (!Number.isFinite(year)) return false;

    const startYear = getYearFromAttributeValue(
        getAttributeValue("Expected Start Date", item),
    );
    const endYear = getYearFromAttributeValue(
        getAttributeValue("Expected Completion Date", item),
    );

    if (startYear == null || endYear == null) return false;
    const years = isDpatScoreRelaxationExempt() ? [year - 1, year] : [year];
    return years.some((activeYear) => startYear <= activeYear && activeYear <= endYear);
}

function buildAuditorGeneralReportLinks(documents) {
    const documentData = documents?.data;
    const documentReports = documents?.reports;
    const auditorGeneralDocs = formatDataGeneral(
        documentData,
        "DPAT | M&E Tool Type",
        "Auditor-General Reports",
    ) || [];

    let auditorGeneralReportLink = "";
    if (auditorGeneralDocs.length > 0) {
        auditorGeneralReportLink = getFileLinkIfExist(
            documentReports,
            "MjPmcbueEdU",
            auditorGeneralDocs[0]?.trackedEntity,
        );
    }

    return [
        {
            title: "Auditor-General Reports",
            link: auditorGeneralReportLink
                ? fileUrl(auditorGeneralReportLink, "MjPmcbueEdU")
                : "Not Uploaded",
        },
    ];
}

function buildProjectRows(instances, reports, selectedYear) {
    const projects = (formatDataGeneral(instances, "Project & Programme Type", "Project") || [])
        .filter((item) => isProjectActiveInYear(item, selectedYear));
    const temp = [];

    projects.forEach((item, index) => {
        const reportLink = getFirstFileLinkIfExist(reports, "i1171kI9OBD", item.trackedEntity, "TrdgYOz3XUL");
        const status = getFirstFileLinkIfExist(reports, "tE3QKB203nh", item.trackedEntity, "rRWa5ghYO35", "status");

        const startDate = getAttributeValue("Expected Start Date", item);
        const endDate = getAttributeValue("Expected Completion Date", item);

        temp.push({
            key: item.trackedEntity || index + 1,
            no: index + 1,
            project: getAttributeValue("Name", item),
            startDate: startDate !== "N/A" ? startDate : "Not Provided",
            endDate: endDate !== "N/A" ? endDate : "Not Provided",
            status: status || "Not Provided",
            report: reportLink ? fileUrl(reportLink, "i1171kI9OBD") : "Not Uploaded",
        });
    });

    let score = 3;
    for (const project of temp) {
        if (project.report === "Not Uploaded" || project.status !== "Completed and in-use") {
            score = 0;
        }
    }
    if (temp.length === 0) {
        score = 0;
    }

    return { projectList: temp, scorei: score };
}

const ContractManagementAndAdmins = forwardRef(({
  year,
  district,
  data,
  documents,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {

    const { Title } = Typography;
    const resolvedYear =
        year != null && typeof year === "object" && !Array.isArray(year)
            ? year.value ?? year.label
            : year;
    const districtId =
        typeof district === "string"
            ? district
            : district?.value?.id ?? district?.id ?? "";
    const [projectList, setProjectList] = useState([]);
    const [documentLinks, setDocumentLinks] = useState([]);
    const [scorei, setScoreI] = useState(0);
    const [maxScore, setMaxScore] = useState(3);

    useImperativeHandle(ref, () => ({
        getData: () => ({
            indicator: "PI1",
            area: "Annual Action Plan Implementation",
            maxScore,
            data,
            projectList,
            links: documentLinks,
            scorei,
        }),
    }));

    const DPAT_SLUG = "pi1.0-1.3";

    const projectColumns = [
        { title: "No", dataIndex: "no", key: "no" },
        { title: "Projects", dataIndex: "project", key: "project" },
        { title: "Start Date", dataIndex: "startDate", key: "startDate" },
        { title: "End Date", dataIndex: "endDate", key: "endDate" },
        { title: "Status", dataIndex: "status", key: "status" },
        {
            title: "Completion Report",
            dataIndex: "report",
            key: "report",
            render: (value) => renderDpatEvidenceLink(value, "View Completion Report"),
        },
    ];

    const documentTableColumns = useMemo(
        () => documentsColumn.map((col) =>
            col.dataIndex === "link"
                ? {
                    ...col,
                    render: (value) => renderDpatEvidenceLink(value, "View Document"),
                }
                : col
        ),
        [],
    );

    const loadFromDhis2 = useCallback(async () => {
        if (!districtId || resolvedYear == null) return;

        try {
            let instances = Array.isArray(data?.data) ? data.data : [];
            let reports = Array.isArray(data?.reports) ? data.reports : [];

            if (!instances.length) {
                const entitiesResp = await axios.get("/tracker/trackedEntities", {
                    params: {
                        orgUnit: districtId,
                        program: PROJECT_PROGRAM,
                        pageSize: 5000,
                    },
                });

                instances = entitiesResp?.data?.instances || [];

                if (instances.length) {
                    const eventsResp = await axios.get("/tracker/events", {
                        params: {
                            program: PROJECT_PROGRAM,
                            orgUnit: districtId,
                            pageSize: 5000,
                        },
                    });
                    reports = eventsResp?.data?.instances || [];
                }
            }

            const { projectList: rows, scorei: score } = buildProjectRows(
                instances,
                reports,
                resolvedYear,
            );
            const docLinks = buildAuditorGeneralReportLinks(documents);
            setProjectList(rows);
            setDocumentLinks(docLinks);
            setScoreI(scoreOnlyIfDocumentLinksComplete(docLinks, score));
        } catch (err) {
            console.error("ContractManagementAndAdmins fetch failed:", err);
            setProjectList([]);
            setDocumentLinks([]);
            setScoreI(0);
        }
    }, [districtId, resolvedYear, data, documents]);

    useDpatIndicatorDataSource({
        isAssessmentStarted,
        savedAssessmentData,
        assessmentStartLoading,
        loadFromDhis2,
        applySnapshot: (snap) => {
            setProjectList(snap?.projectList ?? []);
            setDocumentLinks(snap?.links ?? []);
            setScoreI(snap?.scorei ?? 0);
            setMaxScore(snap?.maxScore ?? 3);
        },
        deps: [year, district, data, documents, loadFromDhis2],
    });

    return (
        <Comment
            data={projectList}
            year={resolvedYear}
            districtId={districtId}
            tableCommentedId={`pi1.0-1.3-${resolvedYear}`}
            hideComment={hideComment}
        >
            {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
                    <DpatIndicatorCmsIntro
                        year={resolvedYear}
                        slug={DPAT_SLUG}
                        mainTitleStyle={{ marginTop: "20px" }}
                    />

                    <IndicatorScoreSection>
                      <MaxScoreDisplay score={maxScore} />
                      <ActualScoreDisplay slug={DPAT_SLUG} label="PI 1.0-1.3 Actual Score" score={scorei} />
                    </IndicatorScoreSection>

                    <Title level={5} style={{ marginTop: "20px" }}>
                        Evidence of project completion and use
                    </Title>
                    <Table
                        columns={projectColumns}
                        dataSource={projectList}
                        pagination={false}
                        bordered
                        rowKey="key"
                    />

                    <Title level={5} style={{ marginTop: "20px" }}>
                        Evidence of Attached Documents
                    </Title>
                    <Table
                        columns={documentTableColumns}
                        dataSource={documentLinks}
                        pagination={false}
                        bordered
                        rowKey="title"
                    />
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
            )}
        </Comment>
    );
});

export default ContractManagementAndAdmins;
